import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import Typography from "@mui/material/Typography"
import { useEffect } from "react"
import { useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"

import Column from "../../../common/components/Column"
import Row, { RowButColumnOnMobile } from "../../../common/components/Row"
import { selectSubscription } from "../../users/reducer"
import InvoicesTable from "./InvoicesTable"
import MlsPurchaseHistoryTab from "./MlsPurchaseHistoryTab"
import ProfileColumn from "./ProfileColumn"
import SettingsSectionCard from "./SettingsSectionCard"
import SubscriptionTab from "./SubscriptionTab"
import { PLAN_BILLING_SECTIONS } from "./settingsTabs"
import StripeForm from "./stripe-form"

/**
 * Self-serve subscription + card management (Stripe) is switched off for
 * every owner today; plans are sold through Zoho. Flip this to bring the
 * Subscription and Payment method sections back.
 */
const SELF_SERVE_BILLING_ENABLED = false

/** Billing, Invoices, Subscription and MLS purchase history on one page. */
export default function PlanBillingTab({ onStripeFormLoadingChanged }) {
  const [searchParams] = useSearchParams()
  const section = searchParams.get("section")

  // Old ?tab=invoices style links arrive with ?section=; scroll to it.
  useEffect(() => {
    if (!section) return
    const element = document.getElementById(planBillingSectionId(section))
    element?.scrollIntoView?.({ behavior: "smooth", block: "start" })
  }, [section])

  return (
    <ProfileColumn sx={{ gap: 4 }}>
      {SELF_SERVE_BILLING_ENABLED && (
        <PlanBillingSection
          section={PLAN_BILLING_SECTIONS.subscription}
          title="Subscription"
        >
          <SubscriptionTab />
        </PlanBillingSection>
      )}
      {SELF_SERVE_BILLING_ENABLED && (
        <PlanBillingSection
          section={PLAN_BILLING_SECTIONS.paymentMethod}
          title="Payment method"
        >
          <SettingsSectionCard>
            <CurrentPaymentInformation />
            <Box>
              <StripeForm onIsLoadingChanged={onStripeFormLoadingChanged} />
            </Box>
          </SettingsSectionCard>
        </PlanBillingSection>
      )}
      <PlanBillingSection
        section={PLAN_BILLING_SECTIONS.invoices}
        title="Invoices"
      >
        <InvoicesTable />
      </PlanBillingSection>
      <PlanBillingSection
        section={PLAN_BILLING_SECTIONS.mlsPurchases}
        title="MLS purchases"
      >
        <MlsPurchaseHistoryTab />
      </PlanBillingSection>
    </ProfileColumn>
  )
}

function planBillingSectionId(section) {
  return `plan-billing-${section}`
}

function PlanBillingSection({ section, title, children }) {
  return (
    <Column
      component="section"
      id={planBillingSectionId(section)}
      aria-label={title}
      sx={{ gap: 1.5, scrollMarginTop: "96px" }}
    >
      <Typography variant="h5">{title}</Typography>
      {children}
    </Column>
  )
}

function CurrentPaymentInformation() {
  const subscription = useSelector(selectSubscription)
  const card = subscription?.card_data
  let paymentInformation = { expiryMonth: null, expiryYear: null, last4: null }
  if (card) {
    const expiryMonth = card.exp_month
    const expiryYear = card.exp_year
    const last4 = card.last4
    if (expiryMonth && expiryYear && last4) {
      paymentInformation = { expiryMonth, expiryYear, last4 }
    }
  }
  if (paymentInformation.expiryMonth) {
    return (
      <Column gap={2}>
        <Typography variant="h5">Current Payment Method</Typography>
        <RowButColumnOnMobile gap={2}>
          <TitleDescription
            title="Card Number"
            description={"●●●● ●●●● ●●●● " + paymentInformation.last4}
          />
          <Box>
            <TitleDescription
              title="Expiry"
              description={`${paymentInformation.expiryMonth} / ${paymentInformation.expiryYear}`}
            />
          </Box>
        </RowButColumnOnMobile>
      </Column>
    )
  }
}

function TitleDescription({ title, description }) {
  return (
    <Row alignItems="center">
      <Typography variant="h6" fontSize="0.9em">
        {title}
      </Typography>
      <Chip label={description} sx={{ ml: 1 }} color="info" />
    </Row>
  )
}
